import { PageHeader } from "../shell/PageHeader";
import { SiteTabBar } from "../components/SiteTabBar";
import {
  useGetV1SitesIdDatapath,
  useGetV1SitesIdNow,
  type DataPathHop as ApiDataPathHop,
  type Figure,
} from "@solarkey/api-client";
import {
  Banner,
  CoverageBar,
  EmptyState,
  ErrorState,
  KpiTile,
  Skeleton,
  StateWord,
  Term,
} from "@solarkey/ui";

const FRESHNESS_LABEL: Record<string, string> = {
  live: "Live",
  stale: "Stale",
  path_down: "Path down",
};

const SOURCE_KIND_LABEL: Record<string, string> = {
  device: "Devices",
  logger: "Logger",
  edge_node: "Edge node",
  site_scada: "Site SCADA",
  cloud_ingest: "Cloud ingest",
  engines: "Engines",
  platform: "Platform",
};

/** The site-level statement enum, in words. The raw token is never shown. */
const SITE_STATEMENT_LABEL: Record<string, string> = {
  DATA_PATH_DOWN:
    "Data path down: no telemetry has arrived from this site within the expected cadence.",
  DATA_PATH_STALE:
    "Data path stale: the newest packet is older than the expected cadence.",
  DATA_PATH_CANNOT_CHECK:
    "We could not read this site's data just now, so its data path cannot be confirmed.",
  DATA_PATH_NO_SOURCE:
    "This site has no telemetry source registered, so there is no data path to check.",
};

const STATEMENT_TONE: Record<string, "path_down" | "warning" | "info"> = {
  DATA_PATH_DOWN: "path_down",
  DATA_PATH_STALE: "warning",
  DATA_PATH_CANNOT_CHECK: "info",
  DATA_PATH_NO_SOURCE: "info",
};

/** What `coverage.basis` means in words — the figure is device level, not signal level. */
const COVERAGE_BASIS_LABEL: Record<string, string> = {
  DEVICE: "Device level",
  PLANT: "Plant",
};

/** A hop the platform has no signal for: named, with a reason, and no freshness. */
type WithheldHop = {
  source_kind?: string | null;
  state?: string | null;
  reason?: { template?: string; params?: Record<string, unknown> } | null;
};

const WITHHELD_HOP_TEXT: Record<string, string> = {
  NOT_MEASURED_NO_LOGGER_SIGNAL:
    "The logger has no signal of its own: its packets are only observable as device telemetry.",
  NOT_MEASURED_ENGINE_HAS_NOT_RUN:
    "The evaluation engines have not produced a result for this site yet.",
  NOT_MEASURED_NO_PLATFORM_HEARTBEAT:
    "The platform publishes no heartbeat of its own, so this hop is not checked.",
  CANNOT_CHECK_TELEMETRY_STORE_UNREACHABLE:
    "This site's data could not be read just now.",
  NOT_MEASURED_NO_TELEMETRY_SOURCE:
    "This site has no telemetry source registered.",
};

export function DataPath({ site }: { site: string }): React.ReactNode {
  const query = useGetV1SitesIdDatapath(site);
  // The consequence tiles state what each FIGURE says about itself; they are not
  // re-derived from coverage in the browser (UX-003 §3.7 rule 8).
  const nowQuery = useGetV1SitesIdNow(site);

  if (query.isLoading) {
    return (
      <div className="flex flex-col gap-density-pad" aria-busy="true">
        <Skeleton variant="text" width="12rem" />
        <Skeleton variant="row" lines={4} />
        <Skeleton variant="row" lines={3} />
      </div>
    );
  }

  if (query.isError || !query.data || query.data.status !== 200) {
    return (
      <ErrorState
        title="Data path is unavailable"
        message="The source chain could not be loaded."
        onRetry={() => void query.refetch()}
      />
    );
  }

  const d = query.data.data.data;
  const statement =
    typeof d.site_statement === "string" && d.site_statement.length > 0
      ? d.site_statement
      : null;
  const apiHops = d.hops ?? [];
  const withheldHops =
    ((d as Record<string, unknown>).hops_withheld as WithheldHop[] | undefined) ?? [];
  const coveragePct =
    typeof d.coverage?.value === "number" ? d.coverage.value : null;
  const coverageBasis = d.coverage?.basis;
  const counts = (d as Record<string, unknown>).coverage_counts as
    | { reporting?: number; monitored?: number }
    | undefined;
  const unmonitored = d.unmonitored ?? [];
  const partiallyMonitored = d.partially_monitored ?? [];
  // Whether the partial-monitoring list may be read as complete. Without this the
  // empty list cannot be told apart from "the signal inventory was unavailable".
  const partialJudgement = (d as Record<string, unknown>).partial_monitoring as
    | { state?: string; reason?: { template?: string } | null }
    | undefined;
  const partialVerified = partialJudgement?.state === "MEASURED";
  const notReporting = [
    ...unmonitored.map((u) => ({ ...u, kind: "unmonitored" as const })),
    ...partiallyMonitored.map((p) => ({
      device_id: p.device_id,
      role_name: p.role_name,
      kind: "partial" as const,
      missing_signals: p.missing_signals,
    })),
  ];

  // The engines hop drives the findings consequence: quiet is not claimed while
  // the engines have not run (UX-005 §4.7 wireframe).
  const enginesHop = apiHops.find((h) => h.source_kind === "engines");
  const enginesWithheld = withheldHops.find((h) => h.source_kind === "engines");
  const nowData =
    nowQuery.data?.status === 200 ? nowQuery.data.data : undefined;

  return (
    <div className="flex flex-col" style={{ gap: 14 }}>
      <PageHeader
        title={`Site ${site} · Data path`}
        scope="How this site's data reaches the platform, and how fresh it is."
        showTimeControl={false}
      />
      <SiteTabBar site={site} />

      {statement && SITE_STATEMENT_LABEL[statement] ? (
        <Banner tone={STATEMENT_TONE[statement] ?? "info"}>
          {SITE_STATEMENT_LABEL[statement]}
        </Banner>
      ) : null}

      {/* ── 1. The chain ── */}
      <section className="sk-section" aria-labelledby="dp-chain">
        <h2 id="dp-chain" className="sk-section__title">The chain</h2>
        {apiHops.length > 0 ? (
          <div className="sk-dp-hops">
            {apiHops.map((hop, i) => (
              <HopCard key={hop.source_kind ?? i} hop={hop} />
            ))}
          </div>
        ) : (
          <EmptyState
            variant="no_data"
            title="No hop could be checked"
            message="Not one hop in the chain has a signal the platform could read this request; each is named below with the reason."
          />
        )}
        {withheldHops.length > 0 ? (
          <ul className="sk-dp-withheld" style={{ margin: "6px 0 0", paddingLeft: 0, listStyle: "none" }}>
            {withheldHops.map((hop, i) => (
              <li
                key={hop.source_kind ?? i}
                className="text-text-muted"
                style={{ fontSize: 11, display: "flex", gap: 6, alignItems: "baseline" }}
              >
                <span style={{ fontWeight: 600 }}>
                  {SOURCE_KIND_LABEL[hop.source_kind ?? ""] ?? hop.source_kind}
                </span>
                <StateWord
                  state={hop.state === "CANNOT_CHECK" ? "CANNOT_CHECK" : "NOT_MEASURED"}
                />
                <span>
                  {WITHHELD_HOP_TEXT[hop.reason?.template ?? ""] ??
                    "This hop is not checked by the platform."}
                </span>
              </li>
            ))}
          </ul>
        ) : null}
      </section>

      {/* ── Below the chain: two columns, as the wireframe lays them out —
          Coverage on the left, the consequences + Not-yet-reporting on the right. */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))", gap: 14, alignItems: "start" }}>
      {/* ── 2. Coverage at device level ──
          The figure is reporting devices / monitored devices. Signal-level coverage
          is a different figure and is not served here; the per-signal gaps appear
          in "Not yet reporting" below. */}
      <section className="sk-section" aria-labelledby="dp-coverage">
        <h2 id="dp-coverage" className="sk-section__title">
          Coverage at device level
        </h2>
        {coveragePct != null ? (
          <div className="sk-dp-coverage-row">
            <CoverageBar pct={coveragePct} />
            {coverageBasis ? (
              <span className="sk-basis-chip">
                {COVERAGE_BASIS_LABEL[String(coverageBasis)] ??
                  String(coverageBasis).replace(/_/g, "-")}
              </span>
            ) : null}
          </div>
        ) : null}
        {d.coverage ? (
          <div style={{ marginTop: 8 }}>
            <KpiTile
              label="Devices reporting"
              figure={d.coverage}
              plain={d.coverage.plain ?? undefined}
            />
            {typeof counts?.reporting === "number" && typeof counts?.monitored === "number" ? (
              <p className="text-text-muted" style={{ fontSize: 11, marginTop: 4 }}>
                {counts.reporting} of {counts.monitored} monitored device
                {counts.monitored === 1 ? "" : "s"} reported within the expected cadence.
              </p>
            ) : null}
          </div>
        ) : (
          <EmptyState variant="no_data" title="No coverage data yet" />
        )}
      </section>

      {/* Right column: consequences, then Not-yet-reporting (wireframe layout). */}
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {/* ── 3. What this does to the figures ──
          Each tile states the SERVED figure's own state; no threshold is applied
          in the browser (the ruling's coverage floor lives in configuration). */}
      <section className="sk-section" aria-labelledby="dp-consequences">
        <h2 id="dp-consequences" className="sk-section__title">
          What this does to the figures
        </h2>
        {nowQuery.isLoading ? (
          <Skeleton variant="row" lines={1} />
        ) : nowData ? (
          <div className="sk-dp-consequence">
            {/* Labelled for what the served figures actually are: the site panel's
                default period, the selected PR variant, technical availability. */}
            <ConsequenceTile
              label="Delivered energy"
              figure={nowData.data.tiles?.energy_today?.delivered}
            />
            <ConsequenceTile label={<Term tkey="pr">PR</Term>} figure={nowData.data.tiles?.pr?.selected} />
            <ConsequenceTile
              label={<><Term tkey="technical_availability">Availability</Term> (technical)</>}
              figure={nowData.data.tiles?.availability?.selected}
            />
            <FindingsTile
              enginesState={enginesHop?.freshness?.state ?? null}
              enginesWithheld={!!enginesWithheld}
              openCount={nowData.data.attention?.length ?? null}
            />
          </div>
        ) : (
          <EmptyState
            variant="no_data"
            title="The site's figures did not load"
            message="The site's figures did not load, so their coverage cannot be shown."
          />
        )}
      </section>

      {/* ── 5. Not yet reporting ── */}
      <section className="sk-section" aria-labelledby="dp-notreporting">
        <h2 id="dp-notreporting" className="sk-section__title">
          Not yet reporting
        </h2>
        {notReporting.length > 0 ? (
          <table className="sk-dp-table">
            <thead>
              <tr>
                <th>Device</th>
                <th>Reason</th>
                {/* An `aria-label` on a `th` is not what a screen reader announces
                    as the column header, so axe rejects an otherwise-empty one
                    (empty-table-header). Visually-hidden TEXT is, and it keeps the
                    column visually blank as the wireframe draws it. */}
                <th><span className="sr-only">Action</span></th>
              </tr>
            </thead>
            <tbody>
              {notReporting.map((dev, i) => {
                // The reason the device isn't reporting, in words — the missing
                // signals when partial, otherwise that it carries no telemetry.
                const reason =
                  dev.kind === "partial"
                    ? dev.missing_signals?.length
                      ? `No packets for ${dev.missing_signals.join(", ")}`
                      : "Partially monitored — a signal its class reports is missing"
                    : "Unmonitored — no telemetry source assigned";
                // The wireframe's per-row action. Disabled until the topology
                // editor, source mapping and work-order flow are wired.
                const action = dev.kind === "unmonitored" ? "Map source" : "Raise work order";
                return (
                  <tr key={dev.device_id ?? i}>
                    <td className="sk-dp-table__id">
                      {dev.role_name ?? dev.device_id ?? <StateWord state="NOT_MEASURED" />}
                    </td>
                    <td>{reason}</td>
                    <td style={{ textAlign: "right" }}>
                      <button
                        type="button"
                        className="btn small"
                        disabled
                        title="Not wired yet — topology editing, source mapping and work orders aren't connected."
                      >
                        {action}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : partialVerified && coveragePct === 100 ? (
          <EmptyState
            variant="verified_quiet"
            title="Every registered device is reporting"
            message="Every monitored device reported within the expected cadence, and none is missing a signal its device class reports."
          />
        ) : (
          <EmptyState
            variant="no_data"
            title="Not checked for every device"
            message={
              partialVerified
                ? "No device is missing a signal, but not every monitored device reported within the expected cadence — see coverage above."
                : "We could not read the per-device reporting detail just now, so we cannot confirm every device is reporting."
            }
          />
        )}
      </section>
      </div>
      </div>
    </div>
  );
}

/** A duration in words, as the wireframe's hop cards read it (9 s · 5 min · 2 h). */
function fmtDuration(s: number): string {
  if (s < 90) return `${Math.round(s)} s`;
  if (s < 5400) return `${Math.round(s / 60)} min`;
  return `${Math.round(s / 3600)} h`;
}

/** The hop's own detail line, from the contract: its counts (reporting over
 *  expected, plus any rejected/quarantined/not-yet), its cadence, and how fresh
 *  its last packet is — the rich per-hop detail the wireframe shows. */
function hopDetail(hop: ApiDataPathHop): string[] {
  const parts: string[] = [];
  const counts = hop.counts as Record<string, number | null> | null | undefined;
  if (counts) {
    const entries = Object.entries(counts).filter(([, v]) => typeof v === "number") as [string, number][];
    const rep = entries.find(([k]) => /reporting$/.test(k));
    const exp = entries.find(([k]) => /expected$/.test(k));
    const used = new Set<string>();
    if (rep) {
      used.add(rep[0]);
      if (exp) {
        used.add(exp[0]);
        parts.push(`${rep[1]} / ${exp[1]} reporting`);
      } else {
        parts.push(`${rep[1]} reporting`);
      }
    }
    for (const [k, v] of entries) {
      if (used.has(k)) continue;
      parts.push(`${v} ${k.replace(/_/g, " ").replace(/^devices /, "")}`);
    }
  }
  const age = hop.freshness?.age_s;
  if (typeof age === "number") parts.push(`last packet ${fmtDuration(age)}`);
  if (typeof hop.expected_cadence_s === "number") parts.push(`cadence ${fmtDuration(hop.expected_cadence_s)}`);
  return parts;
}

function HopCard({ hop }: { hop: ApiDataPathHop }): React.ReactNode {
  const kind = hop.source_kind ?? "unknown";
  const label = SOURCE_KIND_LABEL[kind] ?? kind;
  // A hop whose freshness is absent is NOT live — it was not measured.
  const state = hop.freshness?.state ?? "unknown";
  const stateLabel = FRESHNESS_LABEL[state] ?? "Not measured";
  const detail = hopDetail(hop);

  return (
    <div className="sk-dp-hop" data-state={state}>
      <span className="sk-dp-hop__kind">{label}</span>
      <span className="sk-dp-hop__state">
        <span className="sk-dp-hop__dot" data-state={state} />
        {stateLabel}
      </span>
      {detail.length > 0 ? (
        <span className="sk-dp-hop__age">{detail.join(" · ")}</span>
      ) : null}
    </div>
  );
}

/** Severity of a figure's own state — no local thresholds (ruling §6.1). */
function stateTone(state?: string | null): "ok" | "warning" | "fault" {
  if (state === "MEASURED") return "ok";
  if (state === "PROVISIONAL") return "warning";
  return "fault";
}

function ConsequenceTile({
  label,
  figure,
}: {
  label: React.ReactNode;
  figure?: Figure | null;
}): React.ReactNode {
  if (!figure) {
    return (
      <div className="sk-dp-ctile" data-state="fault">
        <span className="sk-dp-ctile__label">{label}</span>
        <StateWord state="NOT_MEASURED" />
      </div>
    );
  }
  const measured = figure.state === "MEASURED";
  return (
    <div className="sk-dp-ctile" data-state={stateTone(figure.state)}>
      <span className="sk-dp-ctile__label">{label}</span>
      {measured ? (
        <span className="sk-basis-chip">
          {figure.basis ? String(figure.basis).replace(/_/g, "-") : "Measured"}
        </span>
      ) : (
        <StateWord state={figure.state} reason={figure.reason} />
      )}
    </div>
  );
}

/** Findings: quiet is never claimed while the engines have not run. */
function FindingsTile({
  enginesState,
  enginesWithheld,
  openCount,
}: {
  enginesState: string | null;
  enginesWithheld: boolean;
  openCount: number | null;
}): React.ReactNode {
  const enginesOk = !enginesWithheld && enginesState === "live";
  return (
    <div className="sk-dp-ctile" data-state={enginesOk ? "ok" : "warning"}>
      <span className="sk-dp-ctile__label">Findings</span>
      {!enginesOk ? (
        <span className="sk-dp-ctile__note" style={{ fontSize: 11 }}>
          {enginesWithheld
            ? "Not claimed: the engines have not run"
            : "Not claimed: the engines' last result is stale"}
        </span>
      ) : openCount == null ? (
        <StateWord state="NOT_MEASURED" />
      ) : (
        <span className="sk-dp-ctile__note" style={{ fontSize: 11 }}>
          {openCount === 0 ? "No open finding" : `${openCount} open`}
        </span>
      )}
    </div>
  );
}
